import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  AfterViewChecked,
  HostListener,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BackendApiService } from '../../../../../services/backend-api.service';
import { ToasterService } from '../../../../../services/core/toaster.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { UserService } from '../../../../../services/core/user.service';
import { ConfirmationPopupComponent } from '../../../../shared/confirmation-popup/confirmation-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { DocumentImageAnnotatorComponent } from '../../../../document/document-image-annotator/document-image-annotator.component';

// Backend-aligned interfaces
export interface TaskComment {
  id: string | number;
  comment: string; // Backend field name
  creator_id: {
    // Nested object from backend
    first_name: string;
    last_name: string;
  };
  created_at: string; // ISO date string
  attach_files: AttachFile[]; // Backend field name
  reactions?: Reaction[]; // Emoji reactions (future backend integration)
}

export interface AttachFile {
  url: string;
  id?: number;
  name?: string;
  size?: number;
  type?: string;
  file_type?: string; // From API: "jpeg", "pdf", etc.
  original_name?: string; // From API: original file name
}

export interface Reaction {
  emoji: string; // Emoji character
  user_ids: number[]; // Users who reacted
  users?: Array<{ id: number; first_name: string; last_name: string }>; // User details for tooltip
  count: number; // Total count
}

@Component({
    selector: 'app-comments',
    templateUrl: './comments.component.html',
    styleUrls: ['./comments.component.scss'],
    standalone: false
})
export class CommentsComponent
  implements OnInit, AfterViewInit, AfterViewChecked
{
  // Input/Output following ERP patterns
  @Input() taskID!: number;
  @Input() model?: any;
  @Input() isActiveTab: boolean = false; // Track if comments tab is currently active
  @Output() commentAdded = new EventEmitter<void>();

  // ViewChild for scroll container
  @ViewChild('noteWrap', { static: false }) noteWrapElement!: ElementRef;

  // Data properties
  comments: TaskComment[] = [];
  isLoadingComments: boolean = false; // Loading state for skeleton screens
  isSubmittingComment: boolean = false; // Loading state for comment submission
  module_id: number = 7; // TASK_MODULE_ID constant
  attachedFiles: number[] = [];
  attachmentsArr: any[] = [];
  allowUploadFile: boolean = false;
  multipleUpload: boolean = true;
  allowedFileTypes: string[] = ['.jpg', '.jpeg', '.gif', '.png', '.pdf'];

  // Scroll management properties
  private shouldScrollToBottom: boolean = false;
  private previousTabState: boolean = false;

  // Current logged-in user ID for reaction matching
  private currentUserId: number | null = null;

  // Rich toolbar properties
  showEmojiPicker: boolean = false;
  showImageUpload: boolean = false;
  showReactionPicker: { [key: string]: boolean } = {}; // Track picker state per comment
  commonEmojis: string[] = [
    '👍',
    '❤️',
    '😊',
    '😂',
    '🎉',
    '🔥',
    '✅',
    '⭐',
    '👏',
    '💯',
    '🚀',
    '💡',
    '📌',
    '✨',
    '🎯',
    '💪',
  ];
  // Quick reactions for comment bubbles (Slack/GitHub style)
  quickReactions: string[] = ['👍', '❤️', '😂', '🎉', '👀'];

  private readonly emojiToApiKeyMap: { [emoji: string]: string } = {
    '❤️': 'Love',
    '👍': 'Thumb',
    '😂': 'Laugh',
    '🎉': 'Best',
    '👀': 'Eyes',
    '🔥': 'Fire',
    '✅': 'Check',
    '⭐': 'Star',
    '👏': 'Clap',
    '💯': 'Hundred',
    '🚀': 'Rocket',
    '💡': 'Idea',
    '📌': 'Pin',
    '✨': 'Sparkle',
    '🎯': 'Target',
    '💪': 'Strong',
  };

  private readonly apiKeyToEmojiMap: { [key: string]: string } = Object.keys(
    this.emojiToApiKeyMap
  ).reduce((acc: { [key: string]: string }, emoji: string) => {
    const apiKey = this.emojiToApiKeyMap[emoji];
    acc[apiKey.toLowerCase()] = emoji;
    return acc;
  }, {});

  // Form following existing ERP pattern
  public sectionForm: UntypedFormGroup = new UntypedFormGroup({
    comment: new UntypedFormControl('', [Validators.required]),
    attached_file: new UntypedFormControl([]),
  });

  constructor(
    public backendApiService: BackendApiService,
    public toasterService: ToasterService,
    public dialog: NgDialogAnimationService,
    private userService: UserService,
    private matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Get actual logged-in user ID for reaction matching
    const userId = this.userService.getID();
    this.currentUserId = userId ? Number(userId) : null;

    if (this.taskID) {
      this.getComments();
    }

    // Initialize current login user's avatar inputs
    this.initializeCurrentUserAvatar();
  }

  // Avatar inputs for the composer (logged-in user)
  public commentAvatarFullName: string = '';
  public commentAvatarUrl: string = '';
  public commentAvatarStatusSlug: any | null = null;

  private initializeCurrentUserAvatar(): void {
    try {
      const staff: any = this.userService.getUserStaffDetail();
      const statusRaw: any = this.userService.getUserStatus();

      const firstName = (staff?.first_name || '').toString();
      const lastName = (staff?.last_name || '').toString();
      this.commentAvatarFullName = `${firstName} ${lastName}`.trim();

      // profile_image could be string or object with url
      const img = staff?.profile_image;
      this.commentAvatarUrl = typeof img === 'string' ? img : img?.url || '';

      let statusSlug: any = null;
      if (statusRaw) {
        try {
          // May be a JSON string or plain slug
          const parsed =
            typeof statusRaw === 'string' ? JSON.parse(statusRaw) : statusRaw;
          statusSlug = parsed?.slug ?? parsed;
        } catch (_) {
          statusSlug = statusRaw;
        }
      }
      this.commentAvatarStatusSlug = statusSlug || null;
    } catch (err) {
      // Fail safe: leave defaults
      console.error('Failed to initialize user avatar for comments:', err);
    }
  }

  /**
   * Get comments from backend
   * API: POST tasks/get-comments/${taskID}
   * Response: { data: { data: Comment[] } }
   */
  getComments(): void {
    this.isLoadingComments = true; // Show skeleton
    this.backendApiService
      .postApi(`tasks/get-comments/${this.taskID}`, '')
      .subscribe(
        (response: any) => {
          const rawComments = response?.data?.data || [];
          this.comments = this.transformCommentsFromApi(rawComments);
          this.isLoadingComments = false; // Hide skeleton

          // Trigger scroll to bottom after comments are loaded
          this.shouldScrollToBottom = true;
        },
        (error) => {
          this.isLoadingComments = false; // Hide skeleton on error too
          console.error('Error loading comments:', error);
        }
      );
  }

  /**
   * Add new comment
   * API: POST tasks/add-comment/${taskID}
   * Payload: { comment: string, attachment_ids: number[] }
   */
  addComment(): void {
    if (this.sectionForm.invalid || this.isSubmittingComment) {
      if (this.isSubmittingComment) {
        return; // Prevent multiple submissions
      }
      this.toasterService.showErrorMessage('Please type comment');
      return;
    }

    this.isSubmittingComment = true; // Show spinner

    const payload = {
      comment: this.sectionForm.get('comment')?.value,
      attachment_ids: this.attachedFiles,
    };

    this.backendApiService
      .postApi(`tasks/add-comment/${this.taskID}`, payload)
      .subscribe(
        (response: any) => {
          this.isSubmittingComment = false; // Hide spinner
          this.toasterService.showSuccessMessage('Comment added successfully');
          this.sectionForm.get('comment')?.setValue('');
          this.attachedFiles = [];
          this.attachmentsArr = [];
          this.allowUploadFile = false;
          this.getComments(); // This will trigger scroll via shouldScrollToBottom flag
          this.commentAdded.emit();
        },
        (error: any) => {
          this.isSubmittingComment = false; // Hide spinner on error
          this.toasterService.showError(
            error?.error?.message || 'Failed to add comment'
          );
        }
      );
  }

  private transformCommentsFromApi(rawComments: any[]): TaskComment[] {
    if (!Array.isArray(rawComments)) {
      return [];
    }

    return rawComments.map((comment: any) => ({
      ...comment,
      reactions: this.groupReactions(comment?.reactions),
    }));
  }

  private groupReactions(rawReactions: any): Reaction[] {
    if (!Array.isArray(rawReactions)) {
      return [];
    }

    const grouped = new Map<
      string,
      {
        userIds: Set<number>;
        users: Map<
          number,
          { id: number; first_name: string; last_name: string }
        >;
      }
    >();

    rawReactions.forEach((reaction: any) => {
      const rawType = reaction?.reaction_type || '';
      const emoji =
        this.apiKeyToEmojiMap[rawType?.toLowerCase()] ||
        reaction?.emoji ||
        '👍';
      const userId = reaction?.user?.id;
      const user = reaction?.user;

      if (!grouped.has(emoji)) {
        grouped.set(emoji, {
          userIds: new Set<number>(),
          users: new Map<
            number,
            { id: number; first_name: string; last_name: string }
          >(),
        });
      }

      const group = grouped.get(emoji)!;
      if (typeof userId === 'number') {
        group.userIds.add(userId);
        // Store user details for tooltip
        if (user && user.first_name && user.last_name) {
          group.users.set(userId, {
            id: userId,
            first_name: user.first_name,
            last_name: user.last_name,
          });
        }
      }
    });

    return Array.from(grouped.entries()).map(([emoji, group]) => ({
      emoji,
      user_ids: Array.from(group.userIds),
      users: Array.from(group.users.values()),
      count: group.userIds.size || 0,
    }));
  }

  /**
   * Handle file attachment callback from app-attachment component
   * Following existing ERP pattern from view-task-detail
   */
  attachment(event: any): void {
    const data = event?.attachment;
    if (Array.isArray(data)) {
      this.attachmentsArr = data;
      const attachmentIds = this.attachmentsArr.map(
        (file: any) => file.attachment_id
      );
      this.attachedFiles = this.attachedFiles.concat(attachmentIds);
    }
  }

  /**
   * Toggle attachment upload visibility
   */
  toggleAttachmentUpload(): void {
    this.allowUploadFile = !this.allowUploadFile;
  }

  /**
   * Handle Enter key submission (Shift+Enter for new line)
   * Following existing ERP pattern
   */
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.addComment();
    }
  }

  /**
   * Get user's full name from backend creator object
   */
  getUserFullName(creator: any): string {
    if (!creator) return 'Unknown User';
    return `${creator.first_name || ''} ${creator.last_name || ''}`.trim();
  }

  /**
   * Get user initials for avatar display
   */
  getUserInitials(creator: any): string {
    if (!creator) return 'U';
    const firstName = creator.first_name || '';
    const lastName = creator.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  /**
   * Format timestamp to "MM/DD/YYYY | HH:MM AM/PM" format
   * Following existing ERP date format pattern
   * Used for tooltips and absolute time display
   */
  formatTimestamp(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${formattedDate} | ${formattedTime}`;
  }

  /**
   * Format timestamp to relative time (modern pattern)
   * Examples: "Just now", "2m ago", "5h ago", "Yesterday", "Jan 15"
   * Based on Slack/Linear/GitHub patterns for better scannability
   */
  getRelativeTime(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Less than 1 minute
    if (diffMins < 1) return 'Just now';

    // Less than 1 hour: show minutes
    if (diffMins < 60) return `${diffMins}m ago`;

    // Less than 24 hours: show hours
    if (diffHours < 24) return `${diffHours}h ago`;

    // Yesterday
    if (diffDays === 1) return 'Yesterday';

    // Less than 7 days: show days
    if (diffDays < 7) return `${diffDays}d ago`;

    // Older than 7 days: show date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Get user avatar URL with fallback to UI Avatars API
   * Uses UI Avatars (https://ui-avatars.com) for generated avatars
   */
  getUserAvatar(creator: any): string {
    if (!creator) {
      return 'https://ui-avatars.com/api/?name=Unknown+User&background=f97316&color=fff&size=128';
    }

    // If backend provides avatar URL, use it (future enhancement)
    if (creator.avatar_url) {
      return creator.avatar_url;
    }

    // Generate avatar from user name
    const firstName = creator.first_name || '';
    const lastName = creator.last_name || '';
    const name = `${firstName}+${lastName}`.trim() || 'User';

    return `https://ui-avatars.com/api/?name=${name}&background=f97316&color=fff&size=128`;
  }

  /**
   * Toggle emoji picker visibility
   */
  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
    // Close image upload if open
    if (this.showEmojiPicker) {
      this.showImageUpload = false;
    }
  }

  /**
   * Insert emoji at cursor position in textarea
   */
  insertEmoji(emoji: string): void {
    const currentValue = this.sectionForm.get('comment')?.value || '';
    this.sectionForm.get('comment')?.setValue(currentValue + emoji);
    this.showEmojiPicker = false;

    // Focus back on textarea
    this.focusCommentInput();
  }

  /**
   * Toggle image upload interface
   * Placeholder for future image upload feature
   */
  toggleImageUpload(): void {
    this.showImageUpload = !this.showImageUpload;
    // Close emoji picker if open
    if (this.showImageUpload) {
      this.showEmojiPicker = false;
    }
    this.toasterService.showInfo('', 'Image upload feature coming soon');
  }

  /**
   * Insert @mention trigger for future mention feature
   * Placeholder for Syncfusion Mention integration
   */
  insertMention(): void {
    const currentValue = this.sectionForm.get('comment')?.value || '';
    this.sectionForm.get('comment')?.setValue(currentValue + '@');
    this.focusCommentInput();
    this.toasterService.showInfo('', 'Mention feature coming soon');
  }

  /**
   * Reply to a specific comment
   * Placeholder for future threaded replies feature
   */
  replyToComment(commentId: string | number): void {
    console.log('Reply to comment:', commentId);
    this.toasterService.showInfo('', 'Reply feature coming soon');
    // Future: Set reply context and focus input
  }

  /**
   * Toggle reaction picker for a specific comment
   */
  toggleReactionPicker(commentId: string | number): void {
    const key = String(commentId);
    this.showReactionPicker[key] = !this.showReactionPicker[key];
  }

  /**
   * Close any open reaction picker when clicking outside of it
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!Object.values(this.showReactionPicker).some((isOpen) => isOpen)) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      this.closeAllReactionPickers();
      return;
    }

    const clickedInsidePicker = target.closest('.reaction-picker');
    const clickedTrigger = target.closest('.add-reaction-btn');

    if (clickedInsidePicker || clickedTrigger) {
      return;
    }

    this.closeAllReactionPickers();
  }

  private closeAllReactionPickers(): void {
    Object.keys(this.showReactionPicker).forEach((key) => {
      this.showReactionPicker[key] = false;
    });
  }

  /**
   * Check if reaction picker is open for a comment
   */
  isReactionPickerOpen(commentId: string | number): boolean {
    return this.showReactionPicker[String(commentId)] || false;
  }

  /**
   * Toggle reaction on a comment (add if not present, remove if present)
   * Modern pattern: Click again to remove your reaction
   */
  toggleReaction(commentId: string | number, emoji: string): void {
    // Check if user ID is available
    if (!this.currentUserId) {
      this.toasterService.showErrorMessage(
        'User ID not found. Please refresh the page.'
      );
      return;
    }

    const comment = this.comments.find((c) => c.id === commentId);
    if (!comment) return;

    // Initialize reactions array if not present
    if (!comment.reactions) {
      comment.reactions = [];
    }

    // Find existing reaction for this emoji
    const existingReaction = comment.reactions.find((r) => r.emoji === emoji);

    if (existingReaction) {
      // Check if current user already reacted
      const userIndex = existingReaction.user_ids.indexOf(this.currentUserId);

      if (userIndex > -1) {
        // Remove user's reaction
        existingReaction.user_ids.splice(userIndex, 1);
        existingReaction.count--;

        // Remove reaction entirely if count is 0
        if (existingReaction.count === 0) {
          comment.reactions = comment.reactions.filter(
            (r) => r.emoji !== emoji
          );
        }
      } else {
        // Add user's reaction
        existingReaction.user_ids.push(this.currentUserId);
        existingReaction.count++;
      }
    } else {
      // Create new reaction
      comment.reactions.push({
        emoji: emoji,
        user_ids: [this.currentUserId],
        count: 1,
      });
    }

    // Close reaction picker
    this.showReactionPicker[String(commentId)] = false;

    // Backend integration (POST only, no re-fetch)
    // Build payload with ONLY reactions that the CURRENT USER has reacted to
    const toBackendKey = (e: string) => this.emojiToApiKeyMap[e] || e;
    const uniqueKeys = Array.from(
      new Set(
        (comment.reactions || [])
          .filter((r) => this.userHasReacted(r))
          .map((r) => toBackendKey(r.emoji))
      )
    );
    const payload = { reactions: uniqueKeys };

    this.backendApiService
      .postApi(`tasks/comments/${commentId}/reactions`, payload)
      .subscribe(
        () => {},
        () => {
          // Rollback on error
          if (!this.currentUserId) return; // Safety check

          const rb = (comment.reactions || []).find((r) => r.emoji === emoji);
          if (rb) {
            const idx = rb.user_ids.indexOf(this.currentUserId);
            if (idx > -1) {
              rb.user_ids.splice(idx, 1);
              rb.count--;
              if (rb.count === 0) {
                comment.reactions = (comment.reactions || []).filter(
                  (r) => r.emoji !== emoji
                );
              }
            } else {
              rb.user_ids.push(this.currentUserId);
              rb.count++;
            }
          } else {
            comment.reactions = comment.reactions || [];
            comment.reactions.push({
              emoji: emoji,
              user_ids: [this.currentUserId],
              count: 1,
            });
          }
        }
      );
  }

  /**
   * Check if current user has reacted with this emoji
   * Used to highlight active reactions
   */
  userHasReacted(reaction: Reaction): boolean {
    if (!this.currentUserId || !reaction?.user_ids) {
      return false;
    }
    return reaction.user_ids.includes(this.currentUserId);
  }

  /**
   * Get emoji name for tooltip (e.g., "heart", "checkmark", "thumbs up")
   */
  private getEmojiName(emoji: string): string {
    const emojiNameMap: { [key: string]: string } = {
      '❤️': 'heart',
      '👍': 'thumbs up',
      '😂': 'laugh',
      '🎉': 'party popper',
      '👀': 'eyes',
      '🔥': 'fire',
      '✅': 'checkmark',
      '⭐': 'star',
      '👏': 'clap',
      '💯': 'hundred',
      '🚀': 'rocket',
      '💡': 'lightbulb',
      '📌': 'pin',
      '✨': 'sparkle',
      '🎯': 'target',
      '💪': 'strong',
      '😊': 'smile',
    };
    return emojiNameMap[emoji] || 'emoji';
  }

  /**
   * Generate Slack-style tooltip text for reaction
   * Format: "❤️ You and Usman reacted on this comment with heart"
   */
  getReactionTooltip(reaction: Reaction): string {
    if (!reaction || !reaction.users || reaction.users.length === 0) {
      return `${reaction.emoji} ${reaction.count} reaction${
        reaction.count > 1 ? 's' : ''
      }`;
    }

    const emojiName = this.getEmojiName(reaction.emoji);
    const users = reaction.users;
    const currentUserIndex = users.findIndex(
      (u) => u.id === this.currentUserId
    );
    const hasCurrentUser = currentUserIndex > -1;

    // Separate current user from others
    let otherUsers = [...users];
    if (hasCurrentUser) {
      otherUsers.splice(currentUserIndex, 1);
    }

    // Build tooltip text
    let tooltipText = `${reaction.emoji} `;

    if (hasCurrentUser && otherUsers.length === 0) {
      // Only current user
      tooltipText += `You reacted on this comment with ${emojiName}`;
    } else if (hasCurrentUser && otherUsers.length === 1) {
      // Current user + 1 other
      const otherName = `${otherUsers[0].first_name} ${otherUsers[0].last_name}`;
      tooltipText += `You and ${otherName} reacted on this comment with ${emojiName}`;
    } else if (hasCurrentUser && otherUsers.length > 1) {
      // Current user + 2+ others
      const firstOther = `${otherUsers[0].first_name} ${otherUsers[0].last_name}`;
      const remainingCount = otherUsers.length - 1;
      tooltipText += `You, ${firstOther}${
        remainingCount > 0
          ? ` and ${remainingCount} other${remainingCount > 1 ? 's' : ''}`
          : ''
      } reacted on this comment with ${emojiName}`;
    } else if (!hasCurrentUser && users.length === 1) {
      // Only 1 other user
      const userName = `${users[0].first_name} ${users[0].last_name}`;
      tooltipText += `${userName} reacted on this comment with ${emojiName}`;
    } else if (!hasCurrentUser && users.length === 2) {
      // 2 other users
      const user1 = `${users[0].first_name} ${users[0].last_name}`;
      const user2 = `${users[1].first_name} ${users[1].last_name}`;
      tooltipText += `${user1} and ${user2} reacted on this comment with ${emojiName}`;
    } else if (!hasCurrentUser && users.length > 2) {
      // 3+ other users
      const user1 = `${users[0].first_name} ${users[0].last_name}`;
      const user2 = `${users[1].first_name} ${users[1].last_name}`;
      const remainingCount = users.length - 2;
      tooltipText += `${user1}, ${user2}${
        remainingCount > 0
          ? ` and ${remainingCount} other${remainingCount > 1 ? 's' : ''}`
          : ''
      } reacted on this comment with ${emojiName}`;
    }

    return tooltipText;
  }

  /**
   * Delete a comment with confirmation
   * Uses existing ERP confirmation dialog pattern
   */
  deleteComment(commentId: string | number): void {
    // Open confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: {
        title: 'Delete Comment',
        message: 'Are you sure you want to delete this comment?',
        description: 'This action cannot be undone.',
        isDanger: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmIcon: 'delete',
      },
    });

    // Handle confirmation result
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Call delete API
        this.backendApiService
          .deleteApi(`tasks/delete-comment/${commentId}`)
          .subscribe(
            (response: any) => {
              // Success handling
              this.toasterService.showSuccessMessage(
                response?.data?.message || 'Comment deleted successfully'
              );
              // Refresh comments list
              this.getComments();
              // Emit event to parent to update comment count
              this.commentAdded.emit();
            },
            (error: any) => {
              // Error handling
              this.toasterService.showError(
                error?.error?.message || 'Failed to delete comment'
              );
            }
          );
      }
    });
  }

  /**
   * Focus the comment input textarea
   * Used by empty state "Add Comment" button
   */
  focusCommentInput(): void {
    setTimeout(() => {
      const textarea = document.querySelector(
        'textarea[formControlName="comment"]'
      ) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  }

  /**
   * Lifecycle hook - called after view initialization
   * Triggers initial scroll if comments are already loaded
   */
  ngAfterViewInit(): void {
    // If comments were loaded before view init, scroll now
    if (this.comments.length > 0 && !this.isLoadingComments) {
      this.shouldScrollToBottom = true;
    }
  }

  /**
   * Lifecycle hook - called after every view check
   * Handles scroll-to-bottom logic based on state changes
   */
  ngAfterViewChecked(): void {
    // Check if tab just became active
    const tabJustActivated = this.isActiveTab && !this.previousTabState;

    // Scroll to bottom if:
    // 1. Flag is set (comments loaded or comment added)
    // 2. Tab just became active and has comments
    if (
      (this.shouldScrollToBottom || tabJustActivated) &&
      this.comments.length > 0
    ) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }

    // Track tab state for next check
    this.previousTabState = this.isActiveTab;
  }

  /**
   * Scroll the comments container to the bottom with smooth animation
   * Shows latest comments/replies first
   */
  private scrollToBottom(): void {
    try {
      if (this.noteWrapElement && this.noteWrapElement.nativeElement) {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          const element = this.noteWrapElement.nativeElement;
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth',
          });
        }, 100);
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  /**
   * Check if file type is an image
   */
  isImageFile(file: AttachFile): boolean {
    if (!file.file_type) {
      return false;
    }
    const imageTypes = ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff'];
    return imageTypes.includes(file.file_type.toLowerCase());
  }

  /**
   * Handle image load error - hide image on error
   */
  handleImageError(event: any): void {
    if (event && event.target) {
      event.target.style.display = 'none';
    }
  }

  /**
   * Open image preview dialog for image files
   */
  openImagePreview(file: AttachFile, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const modifiedModel = {
      url: file.url,
      original_name: file.original_name || file.name || 'image',
      file_type: file.file_type,
      id: file.id
    };

    this.matDialog.open(DocumentImageAnnotatorComponent, {
      width: '92vw',
      maxWidth: '92vw',
      height: '88vh',
      data: modifiedModel,
      panelClass: 'document-image-annotator-panel'
    });
  }
}
